import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form } from 'src/registry/ui/Form';
import { entityMessages } from 'src/features/entity/utils/messages';
import { EntityFormSchemaValues } from 'src/features/entity/formSchema';
import { Separator } from 'src/registry/ui/Separator';
import * as React from 'react';
import { useListEntities } from 'src/features/entity/api/useListEntities';
import { ENTITY_TYPE } from 'src/features/entity/types';
import { useParking } from 'src/providers/ParkingProvider';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { useParams } from 'react-router-dom';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';

type EntityFormProps = {
    form: UseFormReturn<EntityFormSchemaValues>;
    onSubmit: (values: EntityFormSchemaValues) => void;
};

function EntityForm({ onSubmit, form }: EntityFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { id } = useParams();

    const [searchGroup, setSearchGroup] = React.useState<string>('');

    const { data: groupsData } = useListEntities({
        parkingId,
        type: ENTITY_TYPE.ORGANISATION,
        search: searchGroup,
    });

    const allRemainingGroups = React.useMemo(() => {
        return groupsData?.results.filter((group) => group.id !== id) ?? [];
    }, [id, groupsData]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(entityMessages.name)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="phone"
                pattern={/^(?:\+?[0-9]+)?$/}
                type="tel"
                required={false}
                label={formatMessage(entityMessages.phone)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="email"
                type="email"
                required={false}
                label={formatMessage(entityMessages.email)}
            />
            <TextareaFormField<EntityFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(entityMessages.description)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="identifier"
                required={false}
                label={formatMessage(entityMessages.identificationCode)}
            />

            <Separator />
            <SearchSelectFormField
                name="parent"
                control={form.control}
                label={formatMessage(entityMessages.group)}
                items={allRemainingGroups ?? []}
                clearable
                required={false}
                placeholder={`${formatMessage(entityMessages.group)}...`}
                searchPlaceholder={formatMessage(entityMessages.searchGroup)}
                emptyTitle={formatMessage(entityMessages.noGroup)}
                emptyDescription={formatMessage(entityMessages.noGroupFound)}
                getItemValue={(group) => group.id}
                getItemLabel={(group) => group.name}
                getItemKeywords={(group) => [group.name]}
                loadOptions={setSearchGroup}
            />
        </Form>
    );
}

export default EntityForm;
